import React from "react";
import axios from "axios";
import { ToastManager } from "./Toast/ToastManager";
import { useNavigation } from "./hooks/useNavigation";
import {
  useHMSStore,
  selectPeerMetadata,
  selectLocalPeerID,
} from "@100mslive/react-sdk";

import {
  UserPreferencesKeys,
  useUserPreferences,
  defaultPreviewPreference,
} from "./hooks/useUserPreferences";
import { ArrowRightIcon } from "@100mslive/react-icons";
import EventCard from "./EventCard";
import { isMobile } from "react-device-detect";
import { sendAnalyticsEvent } from "../utils/analytics";

const fireViewAllWebengageEvent = () => {
  sendAnalyticsEvent({
    WE: {
      name: "event_click",
      data: { source_module: "event_end_page" },
    },
  });
};

const EventsList = () => {
  const [events, setEvents] = React.useState([]);
  React.useEffect(() => {
    const fetchData = async () => {
      const response = await fetch(
        process.env.REACT_APP_YOCKET_BACKEND + "/events/list/v2"
      );
      if (response.ok) {
        const json = await response.json();
        setEvents(json.data.planned);
      }
    };
    fetchData();
  }, []);
  return (
    <div className="flex flex-col w-full sm:w-[1106px] mx-auto mt-8 overflow-hidden">
      <div className="flex justify-between w-full px-4 sm:px-0">
        <p className="text-base sm:text-xl text-yocket-neutral-900 font-bold">
          {isMobile
            ? "Upcoming Events"
            : "Upcoming Events You Don't Want To Miss!"}
        </p>
        <div
          className="flex text-sm sm:text-base text-yocket-orange-700 space-x-1 cursor-pointer"
          onClick={() => {
            fireViewAllWebengageEvent();
            window.top.location.href =
              process.env.REACT_APP_VUE_HYDROGEN_REDIRECT + "/events";
            ToastManager.clearAllToast();
          }}
        >
          <span>View All</span>
          <ArrowRightIcon className="w-6 h-6" />
        </div>
      </div>
      {events?.length ? (
        <div className="flex sm:flex-wrap gap-4 mt-4 mb-28 h-fit overflow-x-auto px-4 pb-4 sm:pb-4 sm:px-0">
          {events.slice(0, 3).map(eventItem => (
            <EventCard key={eventItem.name} event={eventItem} />
          ))}
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

const PostLeave = () => {
  const navigate = useNavigation();
  const [previewPreference] = useUserPreferences(
    UserPreferencesKeys.PREVIEW,
    defaultPreviewPreference
  );

  const localPeerId = useHMSStore(selectLocalPeerID);
  const metaData = useHMSStore(selectPeerMetadata(localPeerId));

  const eventId = metaData.event_id;
  const eventVenueId = metaData.event_venue_id;

  const isVirtualOfficeSession =
    sessionStorage.getItem("isVirtualOfficeSession") === "true";

  const isWebinar = metaData.meetingType === "webinar";
  const isPCVC = metaData.meetingType === "pc_vc";

  const [showRejoinButton, setShowRejoinButton] = React.useState(false);

  React.useEffect(() => {
    if (isPCVC) {
      setShowRejoinButton(true);
      return;
    }

    if (!isWebinar) {
      setShowRejoinButton(false);
      return;
    }

    const config = {
      method: "get",
      url: `${process.env.REACT_APP_YOCKET_BACKEND}/events/${eventId}/${eventVenueId}/status`,
      headers: {
        "Content-Type": "application/json",
      },
    };

    axios(config)
      .then(response => {
        console.log("event status - ", response?.data);
        if (response?.data && response?.data?.data?.eventStatus === "LIVE") {
          setShowRejoinButton(true);
        }
      })
      .catch(error => {
        console.warn(
          "PostLeave GET error",
          error?.response?.status || error?.message
        );
      });
  }, [eventId, eventVenueId]);

  return (
    <div className="flex flex-col items-center">
      <div
        className="relative flex flex-col items-start sm:items-center justify-center bg-yocket-orange-50 w-full px-5 py-11 sm:py-12"
        style={{
          background: "linear-gradient(180deg, #FDF2E0 0%, #FFF 95.09%)",
          boxShadow: "0 2px 4px 0 rgba(55, 65, 81, 0.10)",
        }}
      >
        <div className="grid place-items-center bg-white h-[60px] w-[60px] shadow-md rounded-full text-3xl font-bold ">
          🙏🏼
        </div>

        <div className="mt-4 sm:mt-5 space-y-1 sm:space-y-2 sm:text-center">
          <p className="text-base sm:text-2xl font-bold sm:flex sm:gap-1">
            <span className="text-yocket-orange-700">
              Thanks for joining in!
            </span>
            <br className="sm:hidden" />
            <span>Hope the call was valuable for you.</span>
          </p>
          <p className="text-xs sm:text-base text-yocket-neutral-500">
            Stay connected and explore exclusive events to maximize your
            opportunities.
          </p>
        </div>

        <div className="flex gap-4 mt-7 sm:mt-8">
          {showRejoinButton ? (
            <div
              className="px-5 py-2.5 w-[105px] text-center rounded-xl bg-white border border-[#CED1D9] text-yocket-neutral-900 font-bold text-sm cursor-pointer"
              onClick={() => {
                let previewUrl = window.sessionStorage.getItem("_jn_lc_lk");
                if (previewUrl) navigate(previewUrl);
                ToastManager.clearAllToast();
              }}
            >
              Rejoin
            </div>
          ) : (
            <></>
          )}
          <div
            className="px-5 py-2.5 w-[140px] text-center rounded-xl text-yocket-neutral-0 bg-yocket-neutral-900 font-bold text-sm cursor-pointer"
            onClick={() => {
              if (isVirtualOfficeSession) {
                sendAnalyticsEvent({
                  WE: {
                    name: "VO_PremiumPage",
                    data: {},
                  },
                });
              }
              let redirectUrl =
                process.env.REACT_APP_VUE_HYDROGEN_REDIRECT + "/premium";
              window.top.location.href = redirectUrl;
              ToastManager.clearAllToast();
            }}
          >
            Back to Yocket
          </div>
        </div>
      </div>
      <EventsList />
    </div>
  );
};

export default PostLeave;
